import React, { useEffect, useCallback, useRef } from "react";
import { Box, useTheme } from "@chakra-ui/react";
import TransparentButton from "./TransparentButton";
import { colors } from "../../constants/GlobalStyles";
import { IconLeftArrow } from "../../icons";
import { ButtonProps as ChakraButtonProps } from "@chakra-ui/react";
import { useTranslate } from "@tolgee/react";
import { pageStore } from "../../zustand/globalStore";

type GoBackArrowButtonProps = ChakraButtonProps & {
  onBack?: () => void;
};

function GoBackArrowButton({ onBack, ...props }: GoBackArrowButtonProps) {
  const { t } = useTranslate();
  const isRightToLeft = useTheme().direction === "rtl";
  const setMobileBackButtonAction = pageStore((state) => state.setMobileBackButtonAction);
  const isRunningAsMobileApp = pageStore((state) => state.isRunningAsMobileApp);

  const latestOnBackRef = useRef<(() => void) | null>(onBack ?? null);

  useEffect(() => {
    latestOnBackRef.current = onBack ?? null;
  }, [onBack]);

  const handleBack = useCallback(() => {
    if (latestOnBackRef.current) {
      latestOnBackRef.current();
      return;
    }
    window.history.back();
  }, []);

  useEffect(() => {
    if (!isRunningAsMobileApp) {
      if (pageStore.getState().mobileBackButtonAction !== null) {
        setMobileBackButtonAction(null);
      }
      return;
    }

    if (pageStore.getState().mobileBackButtonAction !== handleBack) {
      setMobileBackButtonAction(handleBack);
    }

    return () => {
      if (pageStore.getState().mobileBackButtonAction === handleBack) {
        setMobileBackButtonAction(null);
      }
    };
  }, [handleBack, isRunningAsMobileApp, setMobileBackButtonAction]);

  return (
    <Box
      display="flex"
      justifyContent="flex-start"
      marginTop={{ md: "40px", base: "24px" }}
      marginBottom={"8px"}
      {...props}
    >
      <TransparentButton
        aria-label={t("back", "back")}
        padding="0px"
        display="flex"
        alignItems="flex-start"
        onClick={handleBack}
        type="button"
      >
        <Box as="span" display="inline-flex" transform={isRightToLeft ? "scaleX(-1)" : undefined}><IconLeftArrow color={colors.secondaryAction.default} /></Box>
      </TransparentButton>
    </Box>
  );
}

export default React.memo(GoBackArrowButton);
